/**
 * Navigation State Manager
 * Saves and restores navigation context for better UX
 * Allows users to return to exact position after viewing profiles/details
 */

const NAVIGATION_STATE_KEY = 'navigation_state';

export const saveNavigationState = (state) => {
  try {
    const navigationState = {
      ...state,
      timestamp: Date.now(),
      scrollPosition: window.scrollY
    };
    sessionStorage.setItem(NAVIGATION_STATE_KEY, JSON.stringify(navigationState));
  } catch (error) {
    console.error('Failed to save navigation state:', error);
  }
};

export const getNavigationState = () => {
  try {
    const stateStr = sessionStorage.getItem(NAVIGATION_STATE_KEY);
    if (!stateStr) return null;
    
    const state = JSON.parse(stateStr);
    
    // Clear state if older than 30 minutes
    if (Date.now() - state.timestamp > 30 * 60 * 1000) {
      clearNavigationState();
      return null;
    }
    
    return state;
  } catch (error) {
    console.error('Failed to get navigation state:', error);
    return null;
  }
};

export const clearNavigationState = () => {
  try {
    sessionStorage.removeItem(NAVIGATION_STATE_KEY);
  } catch (error) {
    console.error('Failed to clear navigation state:', error);
  }
};

export const hasNavigationState = () => {
  return getNavigationState() !== null;
};

export const navigateWithState = (navigate, path, currentState) => {
  // Extract locationState and selectedProject if provided
  const { locationState, selectedProject, ...stateToSave } = currentState;
  
  // Save navigation state for return
  saveNavigationState(stateToSave);
  
  // If navigating to a resource profile and we have a selectedProject, save it separately
  if (selectedProject && path.includes('/resources/')) {
    const resourceId = path.split('/resources/')[1];
    sessionStorage.setItem(`resource_context_project_${resourceId}`, selectedProject);
  }
  
  // Navigate with optional location state + selectedProject
  const combinedState = {
    ...locationState,
    selectedProject: selectedProject || undefined
  };
  
  if (Object.keys(combinedState).length > 0 && Object.values(combinedState).some(v => v !== undefined)) {
    navigate(path, { state: combinedState });
  } else {
    navigate(path);
  }
};

export const navigateBack = (navigate) => {
  const state = getNavigationState();
  if (state) {
    clearNavigationState();
    navigate(state.path, { 
      state: { 
        restoreState: true,
        ...state  // Pass all saved state fields
      } 
    });
  } else {
    navigate(-1); // Fallback to browser back
  }
};
